import { FunctionComponent, useState } from 'react'
import { styles } from '../../../../styles/themes/style'
import styled from 'styled-components'
import Column from '../../../../components/Column'
import Row from '../../../../components/Row'
import Text from '../../../../components/Text'
import { useOwnApiKey } from './useOwnApiKey'
import { ApiKeyCodeBlock, MASKED_API_KEY } from './ApiKeyVisibility'

interface IApiDocsProps {}

type GroupId = 'connections' | 'csuite' | 'ai'
type EndpointId =
    | 'conn-list' | 'conn-count' | 'conn-company' | 'conn-filter'
    | 'cs-tenure' | 'cs-tenure-status' | 'cs-ws-tenure' | 'cs-ws-tenure-status'
    | 'ai-submit' | 'ai-poll'

interface EndpointDef {
    id: EndpointId
    method: 'GET' | 'POST'
    path: string
    label: string
}

const GROUPS: { id: GroupId; label: string; endpoints: EndpointDef[] }[] = [
    {
        id: 'ai',
        label: 'AI Prompt',
        endpoints: [
            { id: 'ai-submit', method: 'POST', path: '/ai-assistant/api/prompt',                   label: 'Submit prompt' },
            { id: 'ai-poll',   method: 'GET',  path: '/ai-assistant/api/prompt/{conversationId}',  label: 'Poll result' },
        ],
    },
    {
        id: 'connections',
        label: 'Connections',
        endpoints: [
            { id: 'conn-list',    method: 'GET', path: '/profiles/v1/public',          label: 'List connections' },
            { id: 'conn-filter',  method: 'GET', path: '/profiles/v1/public/filter',   label: 'Filter connections' },
            { id: 'conn-count',   method: 'GET', path: '/profiles/v1/public/count',    label: 'Count connections' },
            { id: 'conn-company', method: 'GET', path: '/profiles/v1/public/company',  label: 'By company' },
        ],
    },
    {
        id: 'csuite',
        label: 'C-suite Tenure',
        endpoints: [
            { id: 'cs-tenure',           method: 'GET', path: '/profiles/v1/public/company/csuite-tenure',                  label: 'Start (data API)' },
            { id: 'cs-tenure-status',    method: 'GET', path: '/profiles/v1/public/company/csuite-tenure/status',           label: 'Poll (data API)' },
            { id: 'cs-ws-tenure',        method: 'GET', path: '/profiles/v1/public/company/workspace/csuite-tenure',        label: 'Start (workspace)' },
            { id: 'cs-ws-tenure-status', method: 'GET', path: '/profiles/v1/public/company/workspace/csuite-tenure/status', label: 'Poll (workspace)' },
        ],
    },
]

const DEFAULT_ENDPOINT: Record<GroupId, EndpointId> = {
    ai: 'ai-submit',
    connections: 'conn-list',
    csuite: 'cs-tenure',
}

const ApiDocs: FunctionComponent<IApiDocsProps> = () => {
    const ownApiKey = useOwnApiKey()
    const [keyVisible, setKeyVisible] = useState(false)
    const [activeGroup, setActiveGroup] = useState<GroupId>('ai')
    const [activeEndpoint, setActiveEndpoint] = useState<EndpointId>('ai-submit')

    // Masked in every example below until shown with the eye on the Authentication block.
    const apiKey = ownApiKey ? (keyVisible ? ownApiKey : MASKED_API_KEY) : 'YOUR_API_KEY'
    const baseUrl = 'https://api.leaddelta.com'

    const handleGroupClick = (groupId: GroupId) => {
        setActiveGroup(groupId)
        setActiveEndpoint(DEFAULT_ENDPOINT[groupId])
    }

    // ─── curl helpers ───────────────────────────────────────────────────────────
    const curl = (method: string, path: string, body?: string) =>
        `curl -X ${method} ${baseUrl}${path} \\\n  -H "apikey: ${apiKey}"` +
        (body ? ` \\\n  -H "Content-Type: application/json" \\\n  -d '${body}'` : '')

    return (
        <Container>
            <Column gap='32px'>

                {/* Header */}
                <Column gap='6px'>
                    <HeaderRow gap='10px' alignItems>
                        <Text $heading4>LeadDelta API</Text>
                        <BetaBadge>BETA</BetaBadge>
                    </HeaderRow>
                    <Paragraph>
                        Use the LeadDelta API to query your LinkedIn network. This API is in early access — endpoints and response formats may change as we iterate.
                        Full documentation is available at <DocLink href="https://api.leaddelta.com/public-api-docs" target="_blank" rel="noopener noreferrer">api.leaddelta.com/public-api-docs</DocLink>.
                    </Paragraph>
                </Column>

                {/* Authentication */}
                <Section>
                    <Column gap='12px'>
                        <Text $heading5>Authentication</Text>
                        <Paragraph>
                            All API requests require a valid API key passed in the <InlineCode>apikey</InlineCode> request header.
                            You can generate your API key from the <strong>API Key</strong> tab.
                        </Paragraph>
                        <ApiKeyCodeBlock visible={keyVisible} onToggle={() => setKeyVisible(previous => !previous)} showToggle={!!ownApiKey}>
                            <CodeBlock>
                                <code>apikey: {apiKey}</code>
                            </CodeBlock>
                        </ApiKeyCodeBlock>
                    </Column>
                </Section>

                {/* Group + Endpoint tabs */}
                <Column gap='0'>

                    {/* Group tabs */}
                    <GroupTabRow gap='0'>
                        {GROUPS.map(g => (
                            <GroupTab key={g.id} $active={activeGroup === g.id} onClick={() => handleGroupClick(g.id)}>
                                {g.label}
                            </GroupTab>
                        ))}
                    </GroupTabRow>

                    {/* Endpoint tabs */}
                    <EndpointTabRow gap='0'>
                        {GROUPS.find(g => g.id === activeGroup)!.endpoints.map(ep => (
                            <EndpointTab key={ep.id} $active={activeEndpoint === ep.id} $method={ep.method} onClick={() => setActiveEndpoint(ep.id)}>
                                <EndpointMethodBadge $method={ep.method}>{ep.method}</EndpointMethodBadge>
                                {ep.label}
                            </EndpointTab>
                        ))}
                    </EndpointTabRow>

                    <EndpointContent>
                        {/* ══════════════ CONNECTIONS ══════════════ */}

                        {activeEndpoint === 'conn-list' && (
                            <Column gap='24px'>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgeGet>GET</BadgeGet><code>{baseUrl}/profiles/v1/public</code></EndpointUrl>
                                    <Paragraph>Retrieve all connections from your workspace with pagination support. Returns up to 2000 records per request.</Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Query parameters</SectionLabel>
                                    <ParamTable>
                                        <thead><tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                                        <tbody>
                                            <tr><td><InlineCode>skip</InlineCode></td><td>integer</td><td><BadgeRequired>required</BadgeRequired></td><td>Number of records to skip (pagination offset)</td></tr>
                                            <tr><td><InlineCode>limit</InlineCode></td><td>integer</td><td><BadgeRequired>required</BadgeRequired></td><td>Max records to return. Maximum allowed: 2000</td></tr>
                                        </tbody>
                                    </ParamTable>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example</SectionLabel>
                                    <CodeBlock><code>{curl('GET', '/profiles/v1/public?skip=0&limit=100')}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Response</SectionLabel>
                                    <CodeBlock><code>{`{
  "success": true,
  "data": {
    "connections": [
      {
        "_id": "648742f3291f53afbde22371",
        "firstName": "Jane",
        "lastName": "Smith",
        "headline": "Senior Engineer at Google",
        "company": "Google",
        "jobTitle": "Senior Engineer",
        "location": "San Francisco, CA",
        "linkedinUrl": "https://www.linkedin.com/in/janesmith/",
        "email": "jane@google.com",
        "connectedAt": 1572371802000,
        "tagsArray": [{ "_id": "...", "title": "VIP", "color": "#5964ff" }],
        "notes": [],
        "workspaceName": "My Workspace"
      }
    ],
    "count": 1
  }
}`}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>400</BadgeStatus></td><td>Missing or invalid skip/limit parameters (max 2000)</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>500</BadgeStatus></td><td>Internal server error</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                        {activeEndpoint === 'conn-filter' && (
                            <Column gap='24px'>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgeGet>GET</BadgeGet><code>{baseUrl}/profiles/v1/public/filter</code></EndpointUrl>
                                    <Paragraph>Search and filter connections in your workspace. All text filters use case-insensitive partial matching and can be combined freely. Returns up to 1000 connections per request.</Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Query parameters</SectionLabel>
                                    <ParamTable>
                                        <thead><tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                                        <tbody>
                                            <tr><td><InlineCode>firstName</InlineCode></td><td>string</td><td><BadgeOptional>optional</BadgeOptional></td><td>Filter by first name (partial match)</td></tr>
                                            <tr><td><InlineCode>lastName</InlineCode></td><td>string</td><td><BadgeOptional>optional</BadgeOptional></td><td>Filter by last name (partial match)</td></tr>
                                            <tr><td><InlineCode>company</InlineCode></td><td>string</td><td><BadgeOptional>optional</BadgeOptional></td><td>Filter by company name (partial match)</td></tr>
                                            <tr><td><InlineCode>jobTitle</InlineCode></td><td>string</td><td><BadgeOptional>optional</BadgeOptional></td><td>Filter by job title (partial match)</td></tr>
                                            <tr><td><InlineCode>location</InlineCode></td><td>string</td><td><BadgeOptional>optional</BadgeOptional></td><td>Filter by location (partial match)</td></tr>
                                            <tr><td><InlineCode>headline</InlineCode></td><td>string</td><td><BadgeOptional>optional</BadgeOptional></td><td>Filter by LinkedIn headline (partial match)</td></tr>
                                            <tr><td><InlineCode>tag</InlineCode></td><td>string</td><td><BadgeOptional>optional</BadgeOptional></td><td>Filter by tag name (partial match)</td></tr>
                                            <tr><td><InlineCode>limit</InlineCode></td><td>integer</td><td><BadgeOptional>optional</BadgeOptional></td><td>Max records to return. Default and maximum: 1000</td></tr>
                                        </tbody>
                                    </ParamTable>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example</SectionLabel>
                                    <CodeBlock><code>{curl('GET', '/profiles/v1/public/filter?company=Google&location=San+Francisco&limit=50')}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Response</SectionLabel>
                                    <CodeBlock><code>{`{
  "success": true,
  "data": {
    "connections": [
      "person_firstName: Jane, person_lastName: Smith, person_jobTitle: Senior Engineer,
       person_company: Google, person_location: San Francisco, CA,
       person_publicIdentifier: janesmith, person_internal_id: 648742f3291f53afbde22371,
       tags (person is tagged with): VIP",
      "person_firstName: John, person_lastName: Doe, person_jobTitle: Staff Engineer,
       person_company: Google, person_location: San Francisco, CA,
       person_publicIdentifier: johndoe, person_internal_id: 648742f3291f53afbde22372"
    ],
    "count": 2,
    "limit": 50
  }
}`}</code></CodeBlock>
                                    <TipBox>Connections are returned as structured text strings (e.g. <InlineCode>person_firstName</InlineCode>, <InlineCode>person_jobTitle</InlineCode>), formatted for optimal AI assistant consumption.</TipBox>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>500</BadgeStatus></td><td>Internal server error</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                        {activeEndpoint === 'conn-count' && (
                            <Column gap='24px'>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgeGet>GET</BadgeGet><code>{baseUrl}/profiles/v1/public/count</code></EndpointUrl>
                                    <Paragraph>Returns the total number of connections in your workspace.</Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example</SectionLabel>
                                    <CodeBlock><code>{curl('GET', '/profiles/v1/public/count')}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Response</SectionLabel>
                                    <CodeBlock><code>{`{
  "success": true,
  "data": {
    "count": 1482
  }
}`}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>500</BadgeStatus></td><td>Internal server error</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                        {activeEndpoint === 'conn-company' && (
                            <Column gap='24px'>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgeGet>GET</BadgeGet><code>{baseUrl}/profiles/v1/public/company</code></EndpointUrl>
                                    <Paragraph>Retrieve all connections from your workspace that belong to a specific company. Optionally filter by a team member's email to scope results to their connections only.</Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Query parameters</SectionLabel>
                                    <ParamTable>
                                        <thead><tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                                        <tbody>
                                            <tr><td><InlineCode>companyName</InlineCode></td><td>string</td><td><BadgeRequired>required</BadgeRequired></td><td>Company name to filter connections by</td></tr>
                                            <tr><td><InlineCode>email</InlineCode></td><td>string</td><td><BadgeOptional>optional</BadgeOptional></td><td>Filter to a specific team member's connections</td></tr>
                                        </tbody>
                                    </ParamTable>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example</SectionLabel>
                                    <CodeBlock><code>{curl('GET', '/profiles/v1/public/company?companyName=Google')}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Response</SectionLabel>
                                    <CodeBlock><code>{`{
  "success": true,
  "data": {
    "connections": [
      {
        "_id": "648742f3291f53afbde22371",
        "firstName": "Jane",
        "lastName": "Smith",
        "company": "Google",
        "jobTitle": "Senior Engineer",
        "linkedinUrl": "https://www.linkedin.com/in/janesmith/",
        "tagsArray": [],
        "notes": [],
        "users": [{ "id": "...", "firstName": "Alice", "email": "alice@acme.com" }]
      }
    ],
    "count": 1
  }
}`}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>400</BadgeStatus></td><td>Missing companyName parameter</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>500</BadgeStatus></td><td>Internal server error</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                        {/* ══════════════ C-SUITE TENURE ══════════════ */}

                        {activeEndpoint === 'cs-tenure' && (
                            <Column gap='24px'>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgeGet>GET</BadgeGet><code>{baseUrl}/profiles/v1/public/company/csuite-tenure</code></EndpointUrl>
                                    <Paragraph>
                                        Start an asynchronous calculation of average C-suite tenure for a company using external data partnership data.
                                        Returns a <InlineCode>token</InlineCode> — poll the status endpoint until <InlineCode>jobFinished</InlineCode> is <InlineCode>true</InlineCode>.
                                    </Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Query parameters</SectionLabel>
                                    <ParamTable>
                                        <thead><tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                                        <tbody>
                                            <tr><td><InlineCode>companyName</InlineCode></td><td>string</td><td><BadgeRequired>required</BadgeRequired></td><td>Company to analyse (e.g. "Google")</td></tr>
                                        </tbody>
                                    </ParamTable>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example</SectionLabel>
                                    <CodeBlock><code>{curl('GET', '/profiles/v1/public/company/csuite-tenure?companyName=Google')}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Response</SectionLabel>
                                    <CodeBlock><code>{`{
  "success": true,
  "data": {
    "token": "generated-token-12345"
  }
}`}</code></CodeBlock>
                                    <TipBox>Use the <InlineCode>token</InlineCode> to poll the <strong>Poll (data API)</strong> endpoint.</TipBox>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.orange600}>422</BadgeStatus></td><td>companyName is required</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>502</BadgeStatus></td><td>Data partnership fetch failed</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                        {activeEndpoint === 'cs-tenure-status' && (
                            <Column gap='24px'>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgeGet>GET</BadgeGet><code>{baseUrl}/profiles/v1/public/company/csuite-tenure/status</code></EndpointUrl>
                                    <Paragraph>
                                        Poll the status of a C-suite tenure calculation started via the <strong>Start (data API)</strong> endpoint.
                                        Poll every 2–5 seconds until <InlineCode>jobFinished</InlineCode> is <InlineCode>true</InlineCode>.
                                    </Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Query parameters</SectionLabel>
                                    <ParamTable>
                                        <thead><tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                                        <tbody>
                                            <tr><td><InlineCode>token</InlineCode></td><td>string</td><td><BadgeRequired>required</BadgeRequired></td><td>Token received from the start endpoint</td></tr>
                                        </tbody>
                                    </ParamTable>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example</SectionLabel>
                                    <CodeBlock><code>{curl('GET', '/profiles/v1/public/company/csuite-tenure/status?token=generated-token-12345')}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <Column gap='8px'>
                                        <StatusRow gap='8px' alignItems>
                                            <StatusDot $color={styles.colors.yellow600} />
                                            <SectionLabel>Response — still calculating</SectionLabel>
                                        </StatusRow>
                                        <CodeBlock><code>{`{
  "success": true,
  "data": {
    "jobFinished": false,
    "companyName": "Google"
  }
}`}</code></CodeBlock>
                                    </Column>
                                    <Column gap='8px'>
                                        <StatusRow gap='8px' alignItems>
                                            <StatusDot $color={styles.colors.green600} />
                                            <SectionLabel>Response — completed</SectionLabel>
                                        </StatusRow>
                                        <CodeBlock><code>{`{
  "success": true,
  "data": {
    "jobFinished": true,
    "companyName": "Google",
    "connectionsScanned": 200,
    "peopleFetched": 200,
    "cSuiteCount": 6,
    "missingTenureCount": 0,
    "averageTenureMonths": 61,
    "averageTenureYears": 5.08,
    "people": [ ... ],
    "errorMessage": null
  }
}`}</code></CodeBlock>
                                    </Column>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color='#888'>404</BadgeStatus></td><td>Token not found</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.orange600}>422</BadgeStatus></td><td>token is required</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                        {activeEndpoint === 'cs-ws-tenure' && (
                            <Column gap='24px'>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgeGet>GET</BadgeGet><code>{baseUrl}/profiles/v1/public/company/workspace/csuite-tenure</code></EndpointUrl>
                                    <Paragraph>
                                        Same as <strong>Start (data API)</strong> but uses your workspace connections as the source instead of external data.
                                        Returns a <InlineCode>token</InlineCode> to poll via <strong>Poll (workspace)</strong>.
                                    </Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Query parameters</SectionLabel>
                                    <ParamTable>
                                        <thead><tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                                        <tbody>
                                            <tr><td><InlineCode>companyName</InlineCode></td><td>string</td><td><BadgeRequired>required</BadgeRequired></td><td>Company to analyse (e.g. "Google")</td></tr>
                                        </tbody>
                                    </ParamTable>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example</SectionLabel>
                                    <CodeBlock><code>{curl('GET', '/profiles/v1/public/company/workspace/csuite-tenure?companyName=Google')}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Response</SectionLabel>
                                    <CodeBlock><code>{`{
  "success": true,
  "data": {
    "token": "generated-token-12345"
  }
}`}</code></CodeBlock>
                                    <TipBox>Use the <InlineCode>token</InlineCode> to poll the <strong>Poll (workspace)</strong> endpoint.</TipBox>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.orange600}>422</BadgeStatus></td><td>companyName or workspace context missing</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                        {activeEndpoint === 'cs-ws-tenure-status' && (
                            <Column gap='24px'>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgeGet>GET</BadgeGet><code>{baseUrl}/profiles/v1/public/company/workspace/csuite-tenure/status</code></EndpointUrl>
                                    <Paragraph>
                                        Poll the status of a workspace-based C-suite tenure calculation.
                                        Poll every 2–5 seconds until <InlineCode>jobFinished</InlineCode> is <InlineCode>true</InlineCode>.
                                    </Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Query parameters</SectionLabel>
                                    <ParamTable>
                                        <thead><tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                                        <tbody>
                                            <tr><td><InlineCode>token</InlineCode></td><td>string</td><td><BadgeRequired>required</BadgeRequired></td><td>Token received from the workspace start endpoint</td></tr>
                                        </tbody>
                                    </ParamTable>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example</SectionLabel>
                                    <CodeBlock><code>{curl('GET', '/profiles/v1/public/company/workspace/csuite-tenure/status?token=generated-token-12345')}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <Column gap='8px'>
                                        <StatusRow gap='8px' alignItems>
                                            <StatusDot $color={styles.colors.yellow600} />
                                            <SectionLabel>Response — still calculating</SectionLabel>
                                        </StatusRow>
                                        <CodeBlock><code>{`{
  "success": true,
  "data": {
    "jobFinished": false,
    "companyName": "Google"
  }
}`}</code></CodeBlock>
                                    </Column>
                                    <Column gap='8px'>
                                        <StatusRow gap='8px' alignItems>
                                            <StatusDot $color={styles.colors.green600} />
                                            <SectionLabel>Response — completed</SectionLabel>
                                        </StatusRow>
                                        <CodeBlock><code>{`{
  "success": true,
  "data": {
    "jobFinished": true,
    "companyName": "Google",
    "connectionsScanned": 150,
    "peopleFetched": 150,
    "cSuiteCount": 6,
    "averageTenureMonths": 61,
    "averageTenureYears": 5.08,
    "people": [ ... ],
    "errorMessage": null
  }
}`}</code></CodeBlock>
                                    </Column>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color='#888'>404</BadgeStatus></td><td>Token not found</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.orange600}>422</BadgeStatus></td><td>token is required</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                        {/* ══════════════ AI PROMPT ══════════════ */}

                        {activeEndpoint === 'ai-submit' && (
                            <Column gap='24px'>
                                <PlanNote>
                                    <PlanNoteIcon>ℹ</PlanNoteIcon>
                                    <span>AI Prompt endpoints are available on the <strong>Trial</strong> and <strong>Growth</strong> plans only.</span>
                                </PlanNote>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgePost>POST</BadgePost><code>{baseUrl}/ai-assistant/api/prompt</code></EndpointUrl>
                                    <Paragraph>
                                        Submit a natural-language prompt to the LeadDelta AI assistant. Processing happens asynchronously —
                                        you receive a <InlineCode>conversationId</InlineCode> to poll for results.
                                        To continue a conversation, include a previous <InlineCode>conversationId</InlineCode> in the request body.
                                    </Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Request body</SectionLabel>
                                    <ParamTable>
                                        <thead><tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                                        <tbody>
                                            <tr><td><InlineCode>prompt</InlineCode></td><td>string</td><td><BadgeRequired>required</BadgeRequired></td><td>Natural-language question about your network</td></tr>
                                            <tr><td><InlineCode>conversationId</InlineCode></td><td>string</td><td><BadgeOptional>optional</BadgeOptional></td><td>Include to continue an existing conversation with context</td></tr>
                                        </tbody>
                                    </ParamTable>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example — new prompt</SectionLabel>
                                    <CodeBlock><code>{curl('POST', '/ai-assistant/api/prompt', JSON.stringify({ prompt: 'Show me all my connections from Google in San Francisco' }))}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example — follow-up question</SectionLabel>
                                    <CodeBlock><code>{curl('POST', '/ai-assistant/api/prompt', JSON.stringify({ prompt: 'Which of them have a VP title?', conversationId: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890' }))}</code></CodeBlock>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Response</SectionLabel>
                                    <CodeBlock><code>{`{
  "success": true,
  "data": {
    "conversationId": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "status": "processing"
  }
}`}</code></CodeBlock>
                                    <TipBox>The initial status is always <InlineCode>processing</InlineCode>. Use the returned <InlineCode>conversationId</InlineCode> to poll for the final result.</TipBox>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>400</BadgeStatus></td><td>Missing or invalid prompt</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.orange600}>403</BadgeStatus></td><td>Subscription canceled or plan restriction</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>500</BadgeStatus></td><td>Internal server error</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                        {activeEndpoint === 'ai-poll' && (
                            <Column gap='24px'>
                                <PlanNote>
                                    <PlanNoteIcon>ℹ</PlanNoteIcon>
                                    <span>AI Prompt endpoints are available on the <strong>Trial</strong> and <strong>Growth</strong> plans only.</span>
                                </PlanNote>
                                <Column gap='4px'>
                                    <EndpointUrl><BadgeGet>GET</BadgeGet><code>{baseUrl}/ai-assistant/api/prompt/{'{'+'conversationId}'}</code></EndpointUrl>
                                    <Paragraph>
                                        Check the status of an asynchronous AI prompt using the <InlineCode>conversationId</InlineCode> returned by the POST endpoint.
                                        Poll every 2–5 seconds until status is <InlineCode>completed</InlineCode> or <InlineCode>failed</InlineCode>.
                                    </Paragraph>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Path parameter</SectionLabel>
                                    <ParamTable>
                                        <thead><tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                                        <tbody>
                                            <tr><td><InlineCode>conversationId</InlineCode></td><td>string</td><td><BadgeRequired>required</BadgeRequired></td><td>The ID returned by the POST endpoint</td></tr>
                                        </tbody>
                                    </ParamTable>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Example</SectionLabel>
                                    <CodeBlock><code>{curl('GET', '/ai-assistant/api/prompt/a1b2c3d4-e5f6-7890-abcd-ef1234567890')}</code></CodeBlock>
                                </Column>
                                <Column gap='24px'>
                                    <Column gap='8px'>
                                        <StatusRow gap='8px' alignItems>
                                            <StatusDot $color={styles.colors.yellow600} />
                                            <SectionLabel>Response — processing</SectionLabel>
                                        </StatusRow>
                                        <Text $paragraph3 $lighter>Still working. Poll again after 2–5 seconds.</Text>
                                        <CodeBlock><code>{`{
  "success": true,
  "data": {
    "conversationId": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "status": "processing"
  }
}`}</code></CodeBlock>
                                    </Column>
                                    <Column gap='8px'>
                                        <StatusRow gap='8px' alignItems>
                                            <StatusDot $color={styles.colors.green600} />
                                            <SectionLabel>Response — completed</SectionLabel>
                                        </StatusRow>
                                        <Paragraph>Includes <InlineCode>connections</InlineCode> array with full profile data, tags, enriched contacts, and the AI <InlineCode>response</InlineCode> text.</Paragraph>
                                        <CodeBlock><code>{`{
  "success": true,
  "data": {
    "conversationId": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "status": "completed",
    "connectionsFound": 1,
    "response": "Found 1 connection from Google in San Francisco.",
    "connections": [
      {
        "connectionId": "648742f3291f53afbde22371",
        "firstName": "John",
        "lastName": "Doe",
        "headline": "Senior Software Engineer at Google",
        "jobTitle": "Senior Software Engineer",
        "company": "Google",
        "location": "San Francisco, CA",
        "country": "United States",
        "industry": "Technology",
        "email": "john.doe@gmail.com",
        "phoneNumber": "+1234567890",
        "profilePicture": "https://media.licdn.com/...",
        "publicIdentifier": "john-doe-123456",
        "linkedinUrl": "https://www.linkedin.com/in/john-doe-123456/",
        "languages": ["English", "Spanish"],
        "connectedAt": 1572371802000,
        "jobHistory": [
          {
            "jobTitle": "Senior Engineer",
            "company": "Google",
            "activePeriod": "2020 - Present",
            "companyLogo": "https://media.licdn.com/..."
          }
        ],
        "education": [],
        "experience": [],
        "tags": [
          { "id": "65347eb7246fabcec19453bd", "title": "VIP", "color": "#5964ff" }
        ],
        "autoTags": [
          { "id": "65347eb8246fabcec19453be", "title": "Engineering" }
        ],
        "enrichedEmails": [
          { "type": "business", "value": "john@google.com" }
        ],
        "enrichedPhones": [
          { "value": "+1987654321" }
        ],
        "customFields": [
          { "key": "deal_size", "label": "Deal Size", "type": "number", "value": "50000" }
        ]
      }
    ]
  }
}`}</code></CodeBlock>
                                    </Column>
                                    <Column gap='8px'>
                                        <StatusRow gap='8px' alignItems>
                                            <StatusDot $color={styles.colors.red600} />
                                            <SectionLabel>Response — failed</SectionLabel>
                                        </StatusRow>
                                        <Paragraph>Check the <InlineCode>error</InlineCode> field for details.</Paragraph>
                                        <CodeBlock><code>{`{
  "success": true,
  "data": {
    "conversationId": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "status": "failed",
    "error": "Processing failed due to timeout"
  }
}`}</code></CodeBlock>
                                    </Column>
                                </Column>
                                <Column gap='8px'>
                                    <SectionLabel>Error responses</SectionLabel>
                                    <StatusTable>
                                        <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
                                        <tbody>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>401</BadgeStatus></td><td>Invalid or missing API key</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.orange600}>403</BadgeStatus></td><td>Subscription canceled or plan restriction</td></tr>
                                            <tr><td><BadgeStatus $color='#888'>404</BadgeStatus></td><td>Conversation not found</td></tr>
                                            <tr><td><BadgeStatus $color={styles.colors.red600}>500</BadgeStatus></td><td>Internal server error</td></tr>
                                        </tbody>
                                    </StatusTable>
                                </Column>
                            </Column>
                        )}

                    </EndpointContent>
                </Column>

            </Column>
        </Container>
    )
}

// ─── Styled components ───────────────────────────────────────────────────────

const Container = styled.div`
    width: 100%;
    max-width: 860px;
    padding: 32px 16px 48px 16px;
`
const Section = styled.div`
    padding: 20px;
    background: ${({ theme: { secondaryColor } }) => secondaryColor};
    border-radius: 8px;
    border: 1px solid ${({ theme: { quinaryColor } }) => quinaryColor};
`
const Paragraph = styled.p`
    font-family: NunitoSans;
    font-size: 14px;
    line-height: 22px;
    color: ${({ theme: { nonaryColor } }) => nonaryColor};
    margin: 0;
    display: block;
`
const CodeBlock = styled.pre`
    background: ${styles.colors.black600};
    border: 1px solid ${styles.colors.black500};
    border-radius: 8px;
    padding: 16px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
    line-height: 1.6;
    color: ${styles.colors.white};
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-all;
    margin: 0;

    code {
        background: none;
        padding: 0;
        border: none;
        font-size: inherit;
        color: inherit;
    }
`
const InlineCode = styled.code`
    background: ${({ theme: { tertiaryColor } }) => tertiaryColor};
    border: 1px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    border-radius: 4px;
    padding: 1px 6px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
    color: ${styles.colors.primary600};
    margin: 0 2px;
    white-space: nowrap;
`
const BadgeGray = styled.span`
    background: ${({ theme: { tertiaryColor } }) => tertiaryColor};
    border: 1px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    border-radius: 12px;
    padding: 2px 10px;
    font-family: NunitoSans;
    font-size: 12px;
    font-weight: bold;
    color: ${({ theme: { primaryColor } }) => primaryColor};
`
const BetaBadge = styled.span`
    background: ${styles.colors.yellow600};
    color: ${styles.colors.white};
    border-radius: 4px;
    padding: 1px 7px;
    font-family: NunitoSans;
    font-size: 11px;
    font-weight: bold;
    letter-spacing: 0.05em;
    white-space: nowrap;
    margin-top: 2px;
    flex-shrink: 0;
`
const BadgePost = styled.span`
    background: ${styles.colors.primary100};
    color: ${styles.colors.primary600};
    border-radius: 4px;
    padding: 2px 8px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 11px;
    font-weight: bold;
    flex-shrink: 0;
`
const BadgeGet = styled.span`
    background: ${styles.colors.green200};
    color: ${styles.colors.green600};
    border-radius: 4px;
    padding: 2px 8px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 11px;
    font-weight: bold;
    flex-shrink: 0;
`
const BadgeRequired = styled.span`
    background: ${styles.colors.red200};
    color: ${styles.colors.red600};
    border-radius: 4px;
    padding: 1px 6px;
    font-family: NunitoSans;
    font-size: 11px;
    font-weight: bold;
`
const BadgeOptional = styled.span`
    background: ${({ theme: { tertiaryColor } }) => tertiaryColor};
    color: ${styles.colors.black300};
    border-radius: 4px;
    padding: 1px 6px;
    font-family: NunitoSans;
    font-size: 11px;
`
const BadgeStatus = styled.span<{ $color: string }>`
    background: ${({ $color }) => $color}22;
    color: ${({ $color }) => $color};
    border-radius: 4px;
    padding: 2px 8px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
    font-weight: bold;
    white-space: nowrap;
`
const TipBox = styled.div`
    background: ${styles.colors.primary100};
    border-left: 3px solid ${styles.colors.primary600};
    border-radius: 0 6px 6px 0;
    padding: 10px 14px;
    font-family: NunitoSans;
    font-size: 13px;
    line-height: 1.6;
    color: ${styles.colors.black400};
`
const SectionLabel = styled.span`
    font-family: NunitoSans;
    font-size: 12px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${styles.colors.black300};
`
const EndpointUrl = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    code {
        font-family: 'Courier New', Courier, monospace;
        font-size: 13px;
        color: ${({ theme: { primaryColor } }) => primaryColor};
    }
`
const ParamTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-family: NunitoSans;
    font-size: 13px;
    background: ${styles.colors.white};
    border: 1px solid ${styles.colors.black200};
    border-radius: 8px;
    overflow: hidden;
    table-layout: fixed;

    th, td {
        &:nth-child(1) { width: 200px; }
        &:nth-child(2) { width: 80px; }
        &:nth-child(3) { width: 110px; }
        &:nth-child(4) { width: auto; }
    }

    th {
        text-align: left;
        padding: 8px 12px;
        font-weight: bold;
        color: ${styles.colors.black300};
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        border-bottom: 1px solid ${styles.colors.black200};
        background: ${styles.colors.black100};
    }
    td {
        padding: 10px 12px;
        border-bottom: 1px solid ${styles.colors.black200};
        color: ${styles.colors.black400};
        vertical-align: middle;
    }
    tr:last-child td {
        border-bottom: none;
    }
`
const StatusTable = styled(ParamTable)`
    max-width: 360px;

    th, td {
        &:nth-child(1) { width: 90px; }
        &:nth-child(2) { width: auto; }
    }
`
const GroupTabRow = styled(Row)`
    border-bottom: 2px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    overflow-x: auto;
    &::-webkit-scrollbar { display: none; }
    -ms-overflow-style: none;
    scrollbar-width: none;
`
const GroupTab = styled.div<{ $active: boolean }>`
    padding: 10px 18px;
    cursor: pointer;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: bold;
    color: ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.black300};
    border-bottom: ${({ $active }) => $active ? `2px solid ${styles.colors.primary600}` : '2px solid transparent'};
    margin-bottom: -2px;
    white-space: nowrap;
    transition: color 0.15s;

    &:hover { color: ${styles.colors.primary600}; }
`
const EndpointTabRow = styled(Row)`
    border-bottom: 1px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    margin-top: 4px;
    overflow-x: auto;
    &::-webkit-scrollbar { display: none; }
    -ms-overflow-style: none;
    scrollbar-width: none;
`
const EndpointTab = styled.div<{ $active: boolean; $method: 'GET' | 'POST' }>`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    cursor: pointer;
    font-family: NunitoSans;
    font-size: 13px;
    font-weight: ${({ $active }) => $active ? 'bold' : 'normal'};
    white-space: nowrap;
    transition: color 0.15s;
    color: ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.black300};
    border-bottom: ${({ $active }) => $active ? `2px solid ${styles.colors.primary600}` : '2px solid transparent'};
    margin-bottom: -1px;

    &:hover { color: ${styles.colors.primary600}; }
`
const EndpointMethodBadge = styled.span<{ $method: 'GET' | 'POST' }>`
    font-family: 'Courier New', Courier, monospace;
    font-size: 10px;
    font-weight: bold;
    padding: 1px 5px;
    border-radius: 3px;
    background: ${({ $method }) => $method === 'POST' ? styles.colors.primary100 : styles.colors.green200};
    color: ${({ $method }) => $method === 'POST' ? styles.colors.primary600 : styles.colors.green600};
`
const EndpointContent = styled.div`
    padding: 24px 0;
`
const PlanNote = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    background: ${styles.colors.primary100};
    border: 1px solid ${styles.colors.primary200};
    border-left: 3px solid ${styles.colors.primary600};
    border-radius: 0 6px 6px 0;
    padding: 10px 14px;
    font-family: NunitoSans;
    font-size: 13px;
    line-height: 1.5;
    color: ${styles.colors.black400};

    strong { color: ${styles.colors.primary600}; }
`
const PlanNoteIcon = styled.span`
    font-size: 14px;
    color: ${styles.colors.primary600};
    flex-shrink: 0;
`
const DocLink = styled.a`
    color: ${styles.colors.primary600};
    font-weight: bold;
    text-decoration: none;
    &:hover { text-decoration: underline; }
`
const HeaderRow = styled(Row)``
const StatusRow = styled(Row)``
const StatusDot = styled.div<{ $color: string }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
`

export default ApiDocs
